// Home v2 — hero balance card. Public mode = blue gradient, Private = orange.
// Shows wallet identity, address (copyable), the public/private mode toggle and
// the estimated balance with hide + refresh affordances.
import React, { useCallback, useState } from 'react'
import { Pressable, View } from 'react-native'

import CopyIcon from '@common/assets/svg/CopyIcon'
import Avatar from '@common/components/Avatar'
import useNavigation from '@common/hooks/useNavigation'
import RefreshIcon from '@common/modules/dashboard/components/DashboardOverview/RefreshIcon'
import RailgunMissingPoiUsdPill from '@common/modules/dashboard/components/DashboardOverview/RailgunMissingPoiUsdPill/RailgunMissingPoiUsdPill'
import { RailgunShieldPendingBadge } from '@common/modules/dashboard/components/DashboardOverview/RailgunShieldPendingBadge'
import { DashboardPrivatePendingShieldSlice } from '@common/modules/dashboard/hooks/useDashboardPrivatePendingRow'
import RollingText from '@common/components/RollingText'
import SkeletonLoader from '@common/components/SkeletonLoader'
import Text from '@common/components/Text'
import formatShortAddress from '@common/utils/formatShortAddress'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import { Mode } from '@web/contexts/appModeContext'
import { PrivateModeReason } from '@web/utils/privateModeAvailability'

import { RADII } from '../tokens'
import GrainientBanner from './GrainientBanner'
import { ChevronDownIcon, EyeIcon, EyeOffIcon } from './HomeIcons'
import ModeToggle from './ModeToggle'

type Props = {
  mode: Mode
  onModeChange: (mode: Mode) => void
  privateModeReason: PrivateModeReason
  onRetryRailgunInit: () => void
  accountName: string
  accountAddress: string
  displayAddress: string
  accountPfp: string
  accountIsSmart: boolean
  onAccountPress: () => void
  balanceFormatted: string
  isLoading: boolean
  isSyncing: boolean
  showScanProgress: boolean
  scanningProgress: string
  showFirstBalanceLoadHint: boolean
  shieldPending: DashboardPrivatePendingShieldSlice
  totalMissingPoiUsd: number
  showPrivatePendingRow: boolean
  hidden: boolean
  onToggleHidden: () => void
  onRefresh: () => void
  compact?: boolean
}

const BalanceCard: React.FC<Props> = ({
  mode,
  onModeChange,
  privateModeReason,
  onRetryRailgunInit,
  accountName,
  accountAddress,
  displayAddress,
  accountPfp,
  accountIsSmart,
  onAccountPress,
  balanceFormatted,
  isLoading,
  isSyncing,
  showScanProgress,
  scanningProgress,
  showFirstBalanceLoadHint,
  shieldPending,
  totalMissingPoiUsd,
  showPrivatePendingRow,
  hidden,
  onToggleHidden,
  onRefresh,
  compact = false
}) => {
  const { navigate } = useNavigation()
  const goToActivity = useCallback(() => navigate(WEB_ROUTES.transactions), [navigate])
  const [copied, setCopied] = useState(false)
  const copy = () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigator?.clipboard?.writeText(displayAddress)
    } catch {
      // clipboard unavailable — no-op
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 1200)
  }

  return (
    <View
      style={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: RADII.lg,
        padding: compact ? 14 : 16,
        minHeight: 184
      }}
    >
      {/* animated grain-gradient banner (public = blue, private = orange) */}
      <GrainientBanner mode={mode} borderRadius={RADII.lg} />

      {/* content sits above the canvas */}
      <View style={{ zIndex: 1 }}>
        {/* header: identity + mode toggle */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 16
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: compact ? 6 : 8,
              flexShrink: 1,
              minWidth: 0
            }}
          >
            <Pressable onPress={onAccountPress} hitSlop={8}>
              <Avatar
                pfp={accountPfp}
                address={accountAddress}
                isSmart={accountIsSmart}
                size={32}
                showTooltip={false}
              />
            </Pressable>
            <View style={{ flexShrink: 1, minWidth: 0 }}>
              <Pressable
                onPress={onAccountPress}
                hitSlop={8}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}
              >
                <Text fontSize={14} weight="semiBold" color="#fff" numberOfLines={1}>
                  {accountName}
                </Text>
                <ChevronDownIcon size={16} color="rgba(255,255,255,0.85)" />
              </Pressable>
              {!!displayAddress && (
                <Pressable
                  onPress={copy}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
                >
                  <Text fontSize={12} color="rgba(255,255,255,0.85)">
                    ({formatShortAddress(displayAddress)})
                  </Text>
                  <CopyIcon width={14} height={14} color="rgba(255,255,255,0.75)" />
                  {copied && (
                    <Text fontSize={11} color="rgba(255,255,255,0.95)">
                      Copied
                    </Text>
                  )}
                </Pressable>
              )}
            </View>
          </View>
          <ModeToggle
            mode={mode}
            onModeChange={onModeChange}
            privateModeReason={privateModeReason}
            onRetryRailgunInit={onRetryRailgunInit}
            compact={compact}
          />
        </View>

        {/* balance */}
        <View style={{ gap: 6 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text fontSize={16} weight="semiBold" color="rgba(255,255,255,0.95)">
              Estimated Balance
            </Text>
            <Pressable onPress={onToggleHidden} hitSlop={8}>
              {hidden ? (
                <EyeOffIcon size={18} color="rgba(255,255,255,0.85)" />
              ) : (
                <EyeIcon size={18} color="rgba(255,255,255,0.85)" />
              )}
            </Pressable>
          </View>
          <View
            testID="full-balance"
            style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}
          >
            {isLoading ? (
              <SkeletonLoader width={200} height={compact ? 44 : 60} borderRadius={8} lowOpacity />
            ) : (
              <RollingText
                value={hidden ? '••••••••' : balanceFormatted}
                fontSize={compact ? 36 : 48}
                weight="number_bold"
                color="#fff"
                style={{ letterSpacing: -1 }}
              />
            )}
            <Pressable onPress={onRefresh} hitSlop={8} disabled={isSyncing}>
              <RefreshIcon spin={isSyncing} width={24} height={24} color="rgba(255,255,255,0.9)" />
            </Pressable>
            {showScanProgress && (
              <Text fontSize={14} weight="number_bold" color="#fff">
                {scanningProgress}
              </Text>
            )}
          </View>

          {/* First-time private load hint — appears after 15s of the initial
              balance fetch (mirrors the extension BatchOrFirstBalanceHint). */}
          {showFirstBalanceLoadHint && (
            <Text fontSize={12} color="rgba(255,255,255,0.85)" numberOfLines={2}>
              Please allow up to 15 minutes for the initial load
            </Text>
          )}

          {/* Pending private balance: shield "Processing …" badge + "Incoming $X"
              missing-POI pill. Mirrors the extension DashboardOverview pending row. */}
          {showPrivatePendingRow && (
            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              {(shieldPending.processingMultiCount != null || shieldPending.badgeText != null) && (
                <RailgunShieldPendingBadge
                  processingMultiCount={shieldPending.processingMultiCount}
                  badgeText={shieldPending.badgeText}
                  onActivityPress={goToActivity}
                />
              )}
              <RailgunMissingPoiUsdPill usdTotal={totalMissingPoiUsd} />
            </View>
          )}
        </View>
      </View>
    </View>
  )
}

export default React.memo(BalanceCard)
